import jsCookie from 'js-cookie';

/**
 * This utility module allows an implementation to save transient data on a cookie under a random
 * name; then remove it by retrieving it.
 * For example: You use `save` to save a user email address, then you send the ID you got from
 * `save` to some other part of the application; when calling `get` with that ID, you'll get the
 * real email address and the cookie will be removed.
 */

/**
 * The length of all the IDs generated by the module.
 *
 * @type {Number}
 */
const ID_LENGTH = 12;
/**
 * The prefix that will be added to the cookies' names.
 *
 * @type {String}
 */
const PREFIX = 'ssos-';
/**
 * Generates a random string the module will use as name for a cookie the module will create.
 *
 * @return {String}
 */
const generateRandomName = () => {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  let result = '';
  for (let i = 0; i < ID_LENGTH; i++) {
    result += chars.charAt(Math.floor(Math.random() * chars.length));
  }

  return `${PREFIX}${result}`;
};
/**
 * Safely saves something on a cookie under a random name (if `id` is not specified).
 *
 * @param {String}  value        The value that will be saved on the cookie.
 * @param {?String} [name=null]  A specific cookie name to use, instead of the randomly generated
 *                               one the function uses by default.
 *
 * @return {String} If the value can be saved, it will return the name of the cookie; otherwise, it
 *                  will return the `value`.
 */
export const save = (value, name = null) => {
  let result;
  try {
    const useName = name || generateRandomName();
    const expirationMinutes = 5;
    const secondsInAMinute = 60;
    const millisecondsInASecond = 1000;
    const expires = new Date(
      new Date().getTime() +
      (expirationMinutes * secondsInAMinute * millisecondsInASecond),
    );
    jsCookie.set(useName, value, {
      expires,
      secure: true,
    });
    result = useName;
  } catch {
    result = value;
  }

  return result;
};
/**
 * Retrieves something from a cookie and removes it.
 *
 * @param {String} name          The name of the cookie, usually, the result of `get`.
 * @param {String} [clean=true]  Whether or not to remove the cookie after its value is retrieved.
 * @return {String} The value of the cookie or an empty string if cookie doesn't exist.
 */
export const get = (name, clean = true) => {
  let result;
  try {
    result = jsCookie.get(name) || '';
    if (result && clean) {
      jsCookie.remove(name);
    }
  } catch {
    result = '';
  }

  return result;
};
